<script lang="ts">
  import type { QueueEntry } from '../generated';
  import type { RoomState } from '../room/state.svelte';
  import { formatPercent, queueStatusLabel } from './format';

  const { room }: { room: RoomState } = $props();

  let urlInput = $state('');

  const queue = $derived(room.queue);
  const permissions = $derived(room.permissions);
  const canManage = $derived(permissions?.canManageQueue === true && room.live);
  const canControl = $derived(permissions?.canControlPlayback === true && room.live);
  const adding = $derived(room.isBusy('add-to-queue'));
  const currentEntry = $derived(room.playback.state === 'idle' ? null : room.playback.entry);
  /**
   * The server refuses to move anything ahead of the entry that is playing, so
   * the first reorderable slot is 1 while the head entry is the active one.
   * Offering those buttons anyway would only produce a guaranteed rejection.
   */
  const firstMovable = $derived(queue[0] !== undefined && queue[0].id === currentEntry ? 1 : 0);

  async function submitUrl(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    const url = urlInput.trim();
    if (url === '') return;
    const outcome = await room.run({ type: 'add-to-queue', url });
    // Keep the text on failure so the user can fix it instead of retyping.
    if (outcome.result === 'ok') urlInput = '';
  }

  /**
   * Reorder uses entry IDs plus the revision the list was rendered from, never
   * an index: a concurrent change makes the server reject with `stale-revision`
   * and the fresh snapshot is the recovery (no blind retry).
   */
  function move(index: number, direction: -1 | 1): void {
    const revision = room.revision;
    const entry = queue[index];
    if (revision === null || entry === undefined) return;
    const before = direction === -1 ? queue[index - 1]?.id ?? null : queue[index + 2]?.id ?? null;
    void room.run({
      type: 'reorder-queue',
      entry: entry.id,
      expected_revision: revision,
      before,
    });
  }

  function remove(entry: QueueEntry): void {
    const revision = room.revision;
    if (revision === null) return;
    void room.run({ type: 'remove-from-queue', entry: entry.id, expected_revision: revision });
  }

  function clearQueue(): void {
    const revision = room.revision;
    if (revision === null) return;
    void room.run({ type: 'clear-queue', expected_revision: revision });
  }
</script>

<section aria-labelledby="queue-heading">
  <div class="heading">
    <h2 id="queue-heading">Queue</h2>
    <div class="queue-actions">
      <button
        type="button"
        onclick={() => void room.run({ type: 'skip-current' })}
        disabled={!canControl || currentEntry === null}
        title={canControl ? '' : 'The host limited playback control to controllers.'}
      >
        Skip current
      </button>
      <button
        type="button"
        onclick={clearQueue}
        disabled={!canManage || queue.length === 0}
        title={canManage ? '' : 'The host limited queue management to controllers.'}
      >
        Clear queue
      </button>
    </div>
  </div>

  <form onsubmit={submitUrl}>
    <div class="field">
      <label for="add-url">Add a video URL</label>
      <input
        id="add-url"
        type="text"
        inputmode="url"
        autocomplete="off"
        spellcheck="false"
        placeholder="https://www.youtube.com/watch?v=…"
        bind:value={urlInput}
        aria-describedby="add-url-hint"
      />
      <p id="add-url-hint" class="hint">
        Ordinary on-demand videos. Everyone in the room can add; the same video can be added twice.
      </p>
    </div>
    <button type="submit" class="primary" disabled={adding || !room.live}>
      {adding ? 'Adding…' : 'Add'}
    </button>
  </form>

  {#if queue.length === 0}
    <p class="empty">The queue is empty.</p>
  {:else}
    <ol>
      {#each queue as entry, index (entry.id)}
        <li class:current={entry.id === currentEntry}>
          <div class="entry">
            <p class="title">
              {#if entry.title !== null}
                {entry.title}
              {:else}
                <span class="pending">Fetching title…</span>
              {/if}
              {#if entry.id === currentEntry}<span class="badge">now playing</span>{/if}
            </p>
            <p class="meta">
              added by {entry.added_by.username} ·
              <span class:failed={entry.status.state === 'failed'}>
                {queueStatusLabel(entry.status)}
              </span>
            </p>
            {#if entry.status.state === 'preparing' && entry.status.progress !== null}
              <progress
                value={entry.status.progress}
                max="1"
                aria-label={`Preparation progress ${formatPercent(entry.status.progress)}`}
              ></progress>
            {/if}
          </div>
          <div class="entry-actions">
            <button
              type="button"
              onclick={() => move(index, -1)}
              disabled={!canManage || index <= firstMovable}
              aria-label={`Move ${entry.title ?? 'entry'} up`}
            >
              ↑
            </button>
            <button
              type="button"
              onclick={() => move(index, 1)}
              disabled={!canManage || index < firstMovable || index === queue.length - 1}
              aria-label={`Move ${entry.title ?? 'entry'} down`}
            >
              ↓
            </button>
            {#if entry.status.state === 'failed'}
              <button
                type="button"
                onclick={() => void room.run({ type: 'retry-entry', entry: entry.id })}
                disabled={!canManage}
              >
                Retry
              </button>
            {/if}
            <button
              type="button"
              onclick={() => remove(entry)}
              disabled={!canManage}
              aria-label={`Remove ${entry.title ?? 'entry'}`}
            >
              Remove
            </button>
          </div>
        </li>
      {/each}
    </ol>
  {/if}
</section>

<style>
  .heading {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  .queue-actions {
    display: flex;
    gap: 0.4rem;
  }

  form {
    display: flex;
    gap: 0.75rem;
    align-items: flex-start;
    margin: 0.75rem 0 1rem;
  }

  .field {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
  }

  ol {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  li {
    display: flex;
    gap: 0.75rem;
    align-items: flex-start;
    justify-content: space-between;
    background: var(--wa-surface-raised);
    border: 1px solid var(--wa-border);
    border-radius: 0.4rem;
    padding: 0.55rem 0.7rem;
  }

  li.current {
    border-color: var(--wa-accent);
  }

  .entry {
    min-width: 0;
  }

  .title {
    margin: 0;
    overflow-wrap: anywhere;
  }

  .pending {
    color: var(--wa-text-dim);
    font-style: italic;
  }

  .badge {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--wa-accent);
    border: 1px solid var(--wa-accent);
    border-radius: 0.25rem;
    padding: 0 0.3rem;
    margin-left: 0.4rem;
    white-space: nowrap;
  }

  .meta {
    margin: 0.2rem 0 0;
    font-size: 0.85rem;
    color: var(--wa-text-dim);
  }

  .meta .failed {
    color: var(--wa-danger);
  }

  .entry-actions {
    display: flex;
    gap: 0.3rem;
    flex-shrink: 0;
  }

  .entry-actions button {
    padding: 0.3rem 0.5rem;
    font-size: 0.85rem;
  }

  progress {
    width: 100%;
    max-width: 18rem;
    height: 0.4rem;
    margin-top: 0.35rem;
  }

  .empty {
    color: var(--wa-text-dim);
    margin: 0;
  }
</style>
